import {
  BadgeCheck,
  CircleDollarSign,
  Clock,
  Eye,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  Trash2,
  TrendingUp,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import DetalleIngresoModal from '../components/ingresos/DetalleIngresoModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { obtenerEstadoPago } from '../utils/gastos'
import { limpiarRut } from '../utils/rut'
import {
  formatearFechaIngreso,
  formatearMontoIngreso,
  formatearTipoDocumentoIngreso,
  TIPOS_DOCUMENTO_INGRESO,
} from '../utils/ingresos'

const CAMPOS_INGRESO = `
  id,
  empresa_id,
  cliente_id,
  fecha_ingreso,
  fecha_vencimiento,
  fecha_pago,
  tipo_documento,
  numero_documento,
  descripcion,
  monto_neto,
  monto_iva,
  monto_exento,
  otros_impuestos,
  monto_total,
  medio_pago,
  estado_pago,
  estado_registro,
  observaciones,
  creado_por,
  creado_en,
  actualizado_en,
  cliente:clientes!fk_ingreso_cliente_empresa (
    id,
    razon_social,
    rut
  )
`

function TarjetaResumen({ icono: Icono, etiqueta, valor }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <Icono className="text-emerald-600" />

      <p className="mt-5 text-sm text-slate-500">{etiqueta}</p>

      <p className="mt-1 text-2xl font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

function Ingresos() {
  const { empresaActual } = useEmpresa()
  const location = useLocation()
  const navigate = useNavigate()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const [ingresos, setIngresos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [busqueda, setBusqueda] = useState('')
  const [filtroEstadoPago, setFiltroEstadoPago] = useState('')
  const [filtroTipoDocumento, setFiltroTipoDocumento] =
    useState('')

  const [ingresoDetalle, setIngresoDetalle] = useState(null)
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const esConsulta = empresaActual?.rol === 'consulta'

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setIngresos([])
      setCargando(false)
      cerrarDetalle()
      return
    }

    cargarIngresos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  useEffect(() => {
    if (!location.state) return

    if (location.state.recargar) {
      cargarIngresos()
    }

    navigate('/ingresos', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const cargarIngresos = async () => {
    if (!empresaActual?.id) return

    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('ingresos')
        .select(CAMPOS_INGRESO)
        .eq('empresa_id', empresaActual.id)
        .neq('estado_registro', 'anulado')
        .order('fecha_ingreso', { ascending: false })
        .order('creado_en', { ascending: false })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setIngresos(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar ingresos:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los ingresos.'
      )
      setIngresos([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const confirmarEliminacion = async (ingreso) => {
    actualizarModal({
      procesando: true,
      mostrarCancelar: false,
      textoAceptar: 'Eliminando...',
    })

    try {
      const { data: ingresoAnulado, error: errorAnular } =
        await supabase
          .from('ingresos')
          .update({ estado_registro: 'anulado' })
          .eq('id', ingreso.id)
          .eq('empresa_id', empresaActual.id)
          .select('id')
          .maybeSingle()

      if (errorAnular) {
        throw errorAnular
      }

      if (!ingresoAnulado) {
        throw new Error(
          'No tienes permisos para eliminar este ingreso.'
        )
      }

      if (!montadoRef.current) return

      setIngresos((actuales) =>
        actuales.filter(
          (registro) => registro.id !== ingreso.id
        )
      )

      if (ingresoDetalle?.id === ingreso.id) {
        setDetalleAbierto(false)
        setIngresoDetalle(null)
      }

      abrirModal({
        tipo: 'success',
        titulo: 'Ingreso eliminado',
        mensaje: 'El ingreso fue eliminado correctamente.',
      })
    } catch (errorEliminar) {
      console.error('Error al eliminar ingreso:', errorEliminar)

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo eliminar',
        mensaje:
          errorEliminar.message ||
          'No fue posible eliminar el ingreso.',
      })
    }
  }

  const eliminarIngreso = (ingreso) => {
    abrirModal({
      tipo: 'confirm',
      titulo: 'Eliminar ingreso',
      mensaje:
        '¿Estás seguro de que deseas eliminar este ingreso? El registro se conservará como anulado.',
      textoAceptar: 'Eliminar',
      textoCancelar: 'Cancelar',
      mostrarCancelar: true,
      onAceptar: () => confirmarEliminacion(ingreso),
    })
  }

  const verIngreso = (ingreso) => {
    setIngresoDetalle(ingreso)
    setDetalleAbierto(true)
  }

  const cerrarDetalle = () => {
    setDetalleAbierto(false)
    setIngresoDetalle(null)
  }

  const editarIngreso = (ingresoId) => {
    setDetalleAbierto(false)
    navigate(`/ingresos/${ingresoId}/editar`)
  }

  const ingresosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase()
    const rutTermino = limpiarRut(busqueda)

    return ingresos.filter((ingreso) => {
      const coincideBusqueda =
        !termino ||
        (ingreso.descripcion || '')
          .toLowerCase()
          .includes(termino) ||
        (ingreso.numero_documento || '')
          .toLowerCase()
          .includes(termino) ||
        (ingreso.cliente?.razon_social || '')
          .toLowerCase()
          .includes(termino) ||
        (rutTermino &&
          limpiarRut(ingreso.cliente?.rut).includes(
            rutTermino
          ))

      const coincideEstado =
        !filtroEstadoPago ||
        ingreso.estado_pago === filtroEstadoPago

      const coincideTipo =
        !filtroTipoDocumento ||
        ingreso.tipo_documento === filtroTipoDocumento

      return coincideBusqueda && coincideEstado && coincideTipo
    })
  }, [
    ingresos,
    busqueda,
    filtroEstadoPago,
    filtroTipoDocumento,
  ])

  const resumen = useMemo(() => {
    return ingresosFiltrados.reduce(
      (acumulado, ingreso) => {
        const total = Number(ingreso.monto_total) || 0

        acumulado.cantidad += 1
        acumulado.totalGeneral += total

        if (ingreso.estado_pago === 'pagado') {
          acumulado.totalCobrado += total
        } else {
          // "parcial" también se considera pendiente completo para esta
          // primera versión, porque todavía no existe un módulo de abonos.
          // Cuando exista, aquí se deberá restar lo ya cobrado.
          acumulado.totalPendiente += total
        }

        return acumulado
      },
      {
        cantidad: 0,
        totalGeneral: 0,
        totalCobrado: 0,
        totalPendiente: 0,
      }
    )
  }, [ingresosFiltrados])

  const hayFiltrosActivos =
    Boolean(busqueda.trim()) ||
    Boolean(filtroEstadoPago) ||
    Boolean(filtroTipoDocumento)

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroEstadoPago('')
    setFiltroTipoDocumento('')
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando ingresos...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarIngresos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (ingresos.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <TrendingUp
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay ingresos registrados
          </h3>

          <p className="mt-2 text-slate-500">
            {esConsulta
              ? 'Aún no se han registrado ingresos.'
              : 'Presiona “Nuevo ingreso” para ingresar el primer registro.'}
          </p>

          {!esConsulta && (
            <Link
              to="/ingresos/nuevo"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Ingresar primer ingreso
            </Link>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por descripción, número, cliente o RUT"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <select
            value={filtroEstadoPago}
            onChange={(event) =>
              setFiltroEstadoPago(event.target.value)
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Pago parcial</option>
            <option value="pagado">Pagado</option>
          </select>

          <select
            value={filtroTipoDocumento}
            onChange={(event) =>
              setFiltroTipoDocumento(event.target.value)
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los documentos</option>

            {Object.entries(TIPOS_DOCUMENTO_INGRESO).map(
              ([valor, etiqueta]) => (
                <option key={valor} value={valor}>
                  {etiqueta}
                </option>
              )
            )}
          </select>
        </div>

        {ingresosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron ingresos para los filtros
              aplicados.
            </p>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[960px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Fecha</th>
                  <th className="px-6 py-3">Cliente</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Documento
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Número
                  </th>
                  <th className="px-6 py-3">Descripción</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right">Total</th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {ingresosFiltrados.map((ingreso) => {
                  const estado = obtenerEstadoPago(
                    ingreso.estado_pago
                  )

                  return (
                    <tr key={ingreso.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                        {formatearFechaIngreso(
                          ingreso.fecha_ingreso
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {ingreso.cliente ? (
                          <div>
                            <p className="font-semibold text-slate-900">
                              {ingreso.cliente.razon_social}
                            </p>

                            {ingreso.cliente.rut && (
                              <p className="text-xs text-slate-500">
                                {ingreso.cliente.rut}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">
                            Sin cliente
                          </span>
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {formatearTipoDocumentoIngreso(
                          ingreso.tipo_documento
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {ingreso.numero_documento || '—'}
                      </td>

                      <td
                        className="max-w-xs truncate px-6 py-4 text-slate-600"
                        title={ingreso.descripcion}
                      >
                        {ingreso.descripcion}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 md:table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
                        >
                          {estado.etiqueta}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right font-semibold text-slate-900">
                        {formatearMontoIngreso(
                          ingreso.monto_total
                        )}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => verIngreso(ingreso)}
                            title="Ver"
                            aria-label={`Ver ingreso ${ingreso.descripcion}`}
                            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <Eye size={16} />
                          </button>

                          {!esConsulta && (
                            <button
                              type="button"
                              onClick={() =>
                                editarIngreso(ingreso.id)
                              }
                              title="Editar"
                              aria-label={`Editar ingreso ${ingreso.descripcion}`}
                              className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                              <Pencil size={16} />
                            </button>
                          )}

                          {!esConsulta && (
                            <button
                              type="button"
                              onClick={() =>
                                eliminarIngreso(ingreso)
                              }
                              title="Eliminar"
                              aria-label={`Eliminar ingreso ${ingreso.descripcion}`}
                              className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Administración
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Ingresos
          </h2>

          <p className="mt-2 text-slate-500">
            Ingresos registrados para{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={cargarIngresos}
            disabled={cargando}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={18}
              className={cargando ? 'animate-spin' : ''}
            />
            Actualizar
          </button>

          {!esConsulta && (
            <Link
              to="/ingresos/nuevo"
              className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
            >
              <Plus size={20} />
              Nuevo ingreso
            </Link>
          )}
        </div>
      </div>

      {!cargando && !error && ingresos.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <TarjetaResumen
            icono={ReceiptText}
            etiqueta="Cantidad de ingresos"
            valor={resumen.cantidad}
          />

          <TarjetaResumen
            icono={CircleDollarSign}
            etiqueta="Total ingresos"
            valor={formatearMontoIngreso(resumen.totalGeneral)}
          />

          <TarjetaResumen
            icono={BadgeCheck}
            etiqueta="Total cobrado"
            valor={formatearMontoIngreso(resumen.totalCobrado)}
          />

          <TarjetaResumen
            icono={Clock}
            etiqueta="Total pendiente"
            valor={formatearMontoIngreso(resumen.totalPendiente)}
          />
        </div>
      )}

      {renderContenido()}

      <DetalleIngresoModal
        abierto={detalleAbierto}
        ingreso={ingresoDetalle}
        onCerrar={cerrarDetalle}
        onEditar={esConsulta ? undefined : editarIngreso}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Ingresos
